import React, { FC, useLayoutEffect, useRef, useState } from 'react';
import { Empty } from 'antd';
import moment from 'moment';
import { IAnalyzeChannelsChannelShareWeeklyModel } from 'entities/AnalyzeChannels/AnalyzeChannels.models';
import { CHANNEL_ORDER, getChannelColor } from 'entities/AnalyzeChannels/analyzeChannels.channelColors';

interface IComponentProps {
  // The endpoint can respond with an empty object before any data is aggregated.
  data: Partial<IAnalyzeChannelsChannelShareWeeklyModel> | null;
}

const HEIGHT = 250;
const MARGIN = { top: 18, right: 12, bottom: 32, left: 44 };
const PLOT_HEIGHT = HEIGHT - MARGIN.top - MARGIN.bottom;
const Y_MAX = 120; // axis goes above 100% so a full week still has head room
const Y_TICKS = [0, 20, 40, 60, 80, 100, 120];
const BAR_MAX_WIDTH = 26;
const BAR_WIDTH_RATIO = 0.68; // share of a column slot taken by the bar
const BAR_CORNER = 3; // corner radius of a stacked bar
const HOVER_STROKE = 1; // black outline width on a hovered slice
const TOOLTIP_GAP = 10; // px between the segment top and the tooltip

type ChannelSlice = IAnalyzeChannelsChannelShareWeeklyModel['weeks'][number]['items'][number];

interface ISegmentGeom {
  item: ChannelSlice;
  segTop: number;
  height: number;
}

interface IColumnGeom {
  weekNumber: number;
  barX: number;
  centerX: number;
  stackTop: number;
  segments: ISegmentGeom[];
}

const formatPercent = (value: number) => `${value.toFixed(2)}%`;

// Rectangle path with rounding only on the requested corners — used for the hover outline
// so the top / bottom slice follows the bar's rounded cap while inner slices stay square.
const roundedRectPath = (x: number, y: number, w: number, h: number, roundTop: boolean, roundBottom: boolean) => {
  const rt = roundTop ? Math.min(BAR_CORNER, w / 2, h / 2) : 0;
  const rb = roundBottom ? Math.min(BAR_CORNER, w / 2, h / 2) : 0;
  return [
    `M${x + rt},${y}`,
    `H${x + w - rt}`,
    rt ? `A${rt},${rt} 0 0 1 ${x + w},${y + rt}` : '',
    `V${y + h - rb}`,
    rb ? `A${rb},${rb} 0 0 1 ${x + w - rb},${y + h}` : '',
    `H${x + rb}`,
    rb ? `A${rb},${rb} 0 0 1 ${x},${y + h - rb}` : '',
    `V${y + rt}`,
    rt ? `A${rt},${rt} 0 0 1 ${x + rt},${y}` : '',
    'Z',
  ].join(' ');
};

// "2026-W07" -> 7
const parseWeekNumber = (week: string): number => {
  const match = week.match(/W(\d+)/i);
  return match ? Number(match[1]) : 0;
};

// Keep the channels in a stable visual order regardless of the API payload order.
const orderItems = (items: ChannelSlice[]) =>
  [...items].sort((a, b) => CHANNEL_ORDER.indexOf(a.channel) - CHANNEL_ORDER.indexOf(b.channel));

export const ChannelShareWeeklyChart: FC<IComponentProps> = ({ data }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  // Unique per instance so multiple charts on one page don't share clipPath ids.
  const clipIdRef = useRef(`ac-channel-weekly-clip-${Math.random().toString(36).slice(2, 9)}`);
  const [width, setWidth] = useState(960);
  const [hovered, setHovered] = useState<{ column: number; segment: number } | null>(null);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) {
      return;
    }
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) {
        setWidth(w);
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const weeks = data?.weeks ?? [];

  if (weeks.length === 0) {
    return (
      <div ref={wrapRef} className="ac-channel-chart">
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
      </div>
    );
  }

  // The running week is today's ISO week; fall back to the last column if it isn't in the payload.
  const currentWeekNumber = moment().isoWeek();
  const matchedIndex = weeks.findIndex((weekEntry) => parseWeekNumber(weekEntry.week) === currentWeekNumber);
  const currentWeekIndex = matchedIndex === -1 ? weeks.length - 1 : matchedIndex;

  const plotWidth = Math.max(width - MARGIN.left - MARGIN.right, 0);
  const slotWidth = plotWidth / weeks.length;
  const barWidth = Math.min(slotWidth * BAR_WIDTH_RATIO, BAR_MAX_WIDTH);

  const yFor = (value: number) => MARGIN.top + PLOT_HEIGHT * (1 - value / Y_MAX);
  const baselineY = yFor(0);

  const columnsGeom: IColumnGeom[] = weeks.map((weekEntry, columnIndex) => {
    const barX = MARGIN.left + slotWidth * columnIndex + (slotWidth - barWidth) / 2;

    let cumulative = 0;
    const segments = orderItems(weekEntry.items).map((item) => {
      const segBottom = yFor(cumulative);
      const segTop = yFor(cumulative + item.sharePercent);
      cumulative += item.sharePercent;
      return { item, segTop, height: segBottom - segTop };
    });

    const totalShare = segments.reduce((sum, segment) => sum + segment.item.sharePercent, 0);

    return {
      weekNumber: parseWeekNumber(weekEntry.week),
      barX,
      centerX: barX + barWidth / 2,
      stackTop: yFor(totalShare),
      segments,
    };
  });

  const hoveredGeom = hovered ? columnsGeom[hovered.column] : null;
  const hoveredSegment = hovered && hoveredGeom ? hoveredGeom.segments[hovered.segment] : null;

  // Show every other week number when the columns get too tight to read them all.
  const labelStep = slotWidth < 16 ? 2 : 1;

  return (
    <div ref={wrapRef} className="ac-channel-chart">
      <div className="ac-channel-chart__legend">
        {CHANNEL_ORDER.map((channel) => (
          <span key={channel} className="ac-channel-chart__legend-item">
            <span className="ac-channel-chart__legend-dot" style={{ background: getChannelColor(channel) }} />
            {channel}
          </span>
        ))}
      </div>

      <div className="ac-channel-chart__plot">
        <svg width="100%" height={HEIGHT} viewBox={`0 0 ${width} ${HEIGHT}`} role="img">
          {/* Grid lines + Y axis labels */}
          {Y_TICKS.map((tick) => {
            const y = yFor(tick);
            return (
              <g key={tick}>
                <line
                  x1={MARGIN.left}
                  x2={width - MARGIN.right}
                  y1={y}
                  y2={y}
                  stroke="#dadee7"
                  strokeWidth={1}
                  strokeDasharray={tick === 0 ? undefined : '4 4'}
                />
                <text
                  x={MARGIN.left - 8}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="central"
                  fontSize={14}
                  fontFamily="Poppins-Regular, Poppins, sans-serif"
                  fill="#97a2bb"
                >
                  {tick}%
                </text>
              </g>
            );
          })}

          {/* Current week marker */}
          <text
            x={width - MARGIN.right}
            y={MARGIN.top - 12}
            textAnchor="end"
            dominantBaseline="central"
            fontSize={12}
            fontFamily="Poppins-Regular, Poppins, sans-serif"
            fill="#97a2bb"
          >
            {`Current week: ${currentWeekNumber}`}
          </text>

          {/* Columns */}
          {columnsGeom.map(({ weekNumber, barX, centerX, stackTop, segments }, columnIndex) => {
            const clipId = `${clipIdRef.current}-${columnIndex}`;
            const isCurrentWeek = columnIndex === currentWeekIndex;

            return (
              <g key={weekNumber}>
                {/* Rounds all four corners of the whole stacked bar */}
                <clipPath id={clipId}>
                  <rect
                    x={barX}
                    y={stackTop}
                    width={barWidth}
                    height={Math.max(baselineY - stackTop, 0)}
                    rx={BAR_CORNER}
                    ry={BAR_CORNER}
                  />
                </clipPath>

                {/* Track behind the bar */}
                <rect
                  x={barX}
                  y={yFor(100)}
                  width={barWidth}
                  height={baselineY - yFor(100)}
                  rx={BAR_CORNER}
                  fill="#edeef3"
                />

                <g clipPath={`url(#${clipId})`}>
                  {segments.map(({ item, segTop, height }, segmentIndex) => (
                    <rect
                      key={item.channel}
                      x={barX}
                      y={segTop}
                      width={barWidth}
                      height={Math.max(height, 0)}
                      fill={getChannelColor(item.channel)}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={() => setHovered({ column: columnIndex, segment: segmentIndex })}
                      onMouseLeave={() => setHovered(null)}
                    />
                  ))}
                </g>

                {/* Black outline on the hovered segment — uniform width, follows the bar cap.
                    Slices stack from the bottom, so index 0 is the bottom slice and the last is the top. */}
                {hovered?.column === columnIndex &&
                  hoveredSegment &&
                  (() => {
                    const isTop = hovered.segment === segments.length - 1;
                    const isBottom = hovered.segment === 0;
                    return (
                      <path
                        d={roundedRectPath(
                          barX + HOVER_STROKE / 2,
                          hoveredSegment.segTop + (isTop ? HOVER_STROKE / 2 : 0),
                          barWidth - HOVER_STROKE,
                          Math.max(
                            hoveredSegment.height - (isTop ? HOVER_STROKE / 2 : 0) - (isBottom ? HOVER_STROKE / 2 : 0),
                            0
                          ),
                          isTop,
                          isBottom
                        )}
                        fill="none"
                        stroke="#0f172b"
                        strokeWidth={HOVER_STROKE}
                        strokeLinejoin="round"
                        pointerEvents="none"
                      />
                    );
                  })()}

                {/* X axis label */}
                {(columnIndex % labelStep === 0 || isCurrentWeek) && (
                  <text
                    x={centerX}
                    y={baselineY + 18}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={12}
                    fontFamily="Poppins-Regular, Poppins, sans-serif"
                    fill="#0f172b"
                  >
                    {weekNumber}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {hoveredGeom && hoveredSegment && (
          <div
            className="ac-channel-chart__tooltip"
            style={{ left: hoveredGeom.centerX, top: hoveredSegment.segTop - TOOLTIP_GAP }}
          >
            <div className="ac-channel-chart__tooltip-title">{`Week ${hoveredGeom.weekNumber}`}</div>
            <div className="ac-channel-chart__tooltip-channel">{hoveredSegment.item.channel}</div>
            <div className="ac-channel-chart__tooltip-value">{formatPercent(hoveredSegment.item.sharePercent)}</div>
          </div>
        )}
      </div>
    </div>
  );
};
